import { type TFunction } from 'i18next'

import { type Move } from './models/move.model'
import * as quoteUtils from '../../quotes'
import { convertTemporalPlainDateToJsDate } from '../../../utils/date'

/**
 * get move nb men
 *  1) if there is a job booked, we return these value
 *  2) if there is a selected quote, we return these value
 *  3) at last, we return the requested values
 */
export const getMoveNbMen = (move: Move) => {
  if (move.selectedQuote) {
    return quoteUtils.getQuoteNbMen(move.selectedQuote)
  }
  return move.request.requirements?.nbMen ?? 0
}

/**
 * get move nb men
 *  1) if there is a job booked, we return these value
 *  2) if there is a selected quote, we return these value
 *  3) at last, we return the requested values
 */
export const getMoveNbTruck = (move: Move) => {
  if (move.selectedQuote) {
    return quoteUtils.getQuoteNbTrucks(move.selectedQuote)
  }
  return move.request.requirements?.nbTrucks
}

/**
 * returns move ref date (first job date)
 */
export const getMoveDate = (move: Move) => {
  return getJobDate(move, move.jobs[0].id)
}

/**
 * get job date
 *  1) if there is a job booked, we return these value
 *  2) if there is a selected quote, we return these value
 *  3) at last, we return the requested values
 */
export const getJobDate = (move: Move, jobId: string) => {
  // todo : this should returns a temporal plainDate

  const jobQuote = getQuoteJob(move, jobId)
  if (jobQuote) {
    return quoteUtils.getQuoteJobDate(jobQuote)
  }
  const moveJob = getMoveJob(move, jobId)
  return moveJob?.dateTime?.startDate
    ? convertTemporalPlainDateToJsDate(moveJob.dateTime.startDate)
    : undefined
}

/**
 * get job nb men
 *  1) if there is a job booked, we return these value
 *  2) if there is a selected quote, we return these value
 *  3) at last, we return the requested values
 */
export const getJobNbMen = (move: Move, jobId: string) => {
  const jobQuote = getQuoteJob(move, jobId)
  if (jobQuote) {
    return jobQuote.jobResolvedBranch?.nbMen ?? 0
  }
  const moveJob = getMoveJob(move, jobId)
  const hasMen = !!moveJob?.moving || !!moveJob?.packing
  if (!hasMen) {
    return 0
  }
  return move.request.requirements?.nbMen ?? 0
}

/**
 * get job nb trucks
 *  1) if there is a job booked, we return these value
 *  2) if there is a selected quote, we return these value
 *  3) at last, we return the requested values
 */
export const getJobNbTrucks = (move: Move, jobId: string) => {
  const jobQuote = getQuoteJob(move, jobId)
  if (jobQuote) {
    return jobQuote.jobResolvedBranch?.nbTrucks ?? []
  }
  const moveJob = getMoveJob(move, jobId)
  const hasTruck = !!moveJob?.moving
  if (!hasTruck) {
    return []
  }
  return move.request.requirements?.nbTrucks ?? []
}

/**
 * get job label that decribe the service required for that day
 */
export const getJobLabel = (move: Move, jobId: string, t: TFunction) => {
  const job = getMoveJob(move, jobId)
  if (!job) {
    return null
  }

  const hasPickupLocations = !!job.moving?.pickupLocations?.length
  const hasDeliveryLocations = !!job.moving?.deliveryLocations?.length
  const hasPackingService = !!job.packing?.location
  const pickupOnly = hasPickupLocations && !hasDeliveryLocations
  const deliveryOnly = hasDeliveryLocations && !hasPickupLocations
  const isMoving = hasPickupLocations && hasDeliveryLocations

  const services = []

  if (hasPackingService) {
    services.push(t('moves.jobs.packing'))
  }
  if (pickupOnly) {
    services.push(t('moves.jobs.pickup'))
  }
  if (deliveryOnly) {
    services.push(t('moves.jobs.delivery'))
  }
  if (isMoving) {
    services.push(t('moves.jobs.moving'))
  }

  return services.join(' + ')
}

const getMoveJob = (move: Move, jobId: string) => {
  return move.jobs.find(job => job.id === jobId)
}

const getQuoteJob = (move: Move, jobId: string) => {
  if (!move.selectedQuote) {
    return
  }
  return move.selectedQuote.jobs.find(job => job.jobId === jobId)
}
